import React, { Dispatch, SetStateAction, useEffect, useMemo, useCallback, useRef, JSX } from 'react';
import FallbackSpinner from 'src/@vigo/vigo-spinner';
import { Card, useTheme } from '@mui/material';
import { GridFilterModel } from '@mui/x-data-grid-pro';
import { GridFeatureMode, GridSortModel } from '@mui/x-data-grid-premium';
import { useAttachmentPanel } from 'src/@core/hooks/useAttachmentPanel';
import { useAppDispatch, useAppSelector } from 'src/store/baseHooks';
import { setSelectedEntityId, setSelectedEntityIds } from 'src/store/slices/attachmentsDataSlice';
import { pushNavigationStack, setFlowDataIdArray, setUIFlowMode } from 'src/store/slices/uiFlowSlice';
import { useRouter } from 'next/router';
import { MasterDataFetchCustomQueryParams } from 'src/types/apps/masterdataTypes';
import { useBreadcrumbs } from 'src/@core/hooks/useBreadcrumbs';
import { PermissionType } from 'src/store/slices/uiFlowPermissionsDataSlice';
import useUIFlowPermissions from 'src/@core/hooks/useUIFlowPermissions';
import NewDataGrid from 'src/@vigo/vigo-datagridNew/NewDataGrid';
import {
  DataGridAction,
  DefaultActions,
  GridCustomToolbarComponent,
  NewColumn,
  PagingOptions,
  RemoteOperation
} from 'src/@vigo/vigo-datagridNew/datagrid-types';
import CustomStore from 'devextreme/data/custom_store';
import { CellClickEvent, RowClickEvent, SelectionChangedEvent } from 'devextreme/ui/data_grid_types';
import FilterPanel from 'src/@vigo/vigo-datagridNew/components/FilterPanel';

interface UIFlowListProps {
  dataSource?: any[];
  customStore?: CustomStore<any, any>;
  customFilter?: any;
  pagingOptions?: PagingOptions;
  columns?: NewColumn[];
  defaultActions?: DefaultActions;
  customActions?: DataGridAction[];
  loading: boolean;
  gridLoading?: boolean;
  queryKey?: string;
  listComponentName: string /* Name of list component e.g. CompanyList */;
  enableSearch?: boolean /* Show search box for list. Default: false */;
  enableImport?: boolean;
  enableLayoutReset?: boolean;
  enableExport?: boolean;
  showRowLines?: boolean;
  showColumnLines?: boolean;
  showFilterRow?: boolean;
  showFilterPanel?: boolean;
  showHeaderFilter?: boolean;
  allowMultiSelect?: boolean;
  multiSelectButtons?: JSX.Element[];
  setSelectedRowIds?: Dispatch<SetStateAction<any[]>>;
  onCellClick?: (event: CellClickEvent) => any | undefined;
  onRowClick?: (event: RowClickEvent) => any | undefined;
  filter?: string;
  sort?: string;
  disableSelectionOnClick?: boolean;
  headerButtonGroup?: GridCustomToolbarComponent[];
  filterCollection?: any;
  onFilterItemSelected?: (filter: string, useAsFilter: boolean) => any;
  filterDateRange?: string;
  filterColumn?: string;
  wrapHeader?: boolean;
  hideHeader?: boolean;
  defaultGridState?: any;
  disableVirtualization?: boolean;
  filterModel?: GridFilterModel;
  remoteOperations?: RemoteOperation;
  scrollMode?: 'standard' | 'virtual' | 'infinite';
  onScrollEnd?: (data: any, setIsLoading: Dispatch<SetStateAction<boolean>>) => void;
  sortingMode: GridFeatureMode;
  handleServerSort?: (model: GridSortModel) => void;
  setCustomType?: Dispatch<MasterDataFetchCustomQueryParams>;
  enableSort?: boolean;
  hideFooter?: boolean;
  pageSize?: number;
  onStateChange?: (state: any) => void;
  disableEffects?: boolean;
  contained?: boolean;
  showGroupingPanel?: boolean;
  showColumnPicker?: boolean;
  hideActions?: boolean;
  keyExpr?: string;
  editToolTip?: string;
  AdvancedSearch?: (props: any) => JSX.Element | null;
  ignoreDates?: boolean;
  height?: string;
}

const UIFlowListNew = ({
  columns,
  dataSource,
  customStore,
  pagingOptions,
  defaultActions,
  customActions = [],
  loading,
  contained,
  enableSearch = false,
  enableExport = false,
  enableLayoutReset = false,
  showFilterRow = false,
  showFilterPanel = false,
  showHeaderFilter = false,
  showColumnLines = false,
  showColumnPicker = true,
  // enablePrint = false,
  showRowLines = false,
  allowMultiSelect,
  disableSelectionOnClick = false,
  headerButtonGroup,
  listComponentName,
  defaultGridState,
  disableVirtualization = true,
  filter,
  filterDateRange,
  filterColumn,
  filterCollection,
  remoteOperations,
  scrollMode,
  hideFooter,
  hideHeader = false,
  disableEffects = false,
  showGroupingPanel = false,
  pageSize,
  setSelectedRowIds,
  onCellClick = undefined,
  onRowClick = undefined,
  onFilterItemSelected,
  onStateChange,
  onScrollEnd,
  hideActions,
  customFilter,
  keyExpr,
  editToolTip,
  AdvancedSearch,
  ignoreDates = false,
  height
}: UIFlowListProps) => {
  const uiFlowDispatch = useAppDispatch();
  const uiFlowMode = useAppSelector(state => state.uiflow.uiFlowMode);
  const selectedEntityIds = useAppSelector(state => state.attachmentsData.selectedEntityIds);

  const router = useRouter();
  const theme = useTheme();

  const { setAttacmentCount, resetAttachmentCount } = useAttachmentPanel();
  const { getUiComponentPermissionByType } = useUIFlowPermissions();
  const { breadcrumbs } = useBreadcrumbs();

  const filterPanelRef = useRef<HTMLDivElement | null>(null);

  const activeComponentId = useAppSelector(state => state.uiflow.activeTabState.activeComponentId);

  const determineUIFlowBasePath = () => {
    let index = breadcrumbs.findIndex(breadcrumb => breadcrumb?.title === 'Admin Centre');

    if (index > -1) {
      return 'uimodule/AdminCentre';
    }

    index = breadcrumbs.findIndex(breadcrumb => breadcrumb?.title === 'Warehouse');

    if (index > -1) {
      return 'uimodule/Warehouse';
    }

    return 'uiflow';
  };

  const gridRowClicked = useCallback(
    (event: RowClickEvent) => {
      if (onRowClick) {
        onRowClick(event);
      }
      const { key, data } = event;

      selectedEntityIds.length === 0 &&
        setAttacmentCount({ type: 'setAttachmentCount', payload: data?.attachmentCount ?? 0 });

      uiFlowDispatch(setSelectedEntityId((key as string) ?? ''));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRowClick]
  );

  const multiSelectRowsIdsChange = useCallback(
    (e: SelectionChangedEvent, items: any[] = []) => {
      const { selectedRowKeys: ids } = e;
      if (typeof setSelectedRowIds === 'function') {
        setSelectedRowIds(ids);
      }
      if (ids.length === 1) {
        const selectedRecord = items.filter((row: any) => ids.includes(row.id));
        if (selectedRecord.length > 0) {
          setAttacmentCount({ type: 'setAttachmentCount', payload: selectedRecord[0].attachmentCount });
        }
      } else {
        setAttacmentCount({ type: 'setAttachmentCount', payload: 0 });
      }
      uiFlowDispatch(setSelectedEntityIds(ids ?? []));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSource]
  );

  const setUIFlowDataIdArray = (data: any[]) => {
    uiFlowDispatch(setFlowDataIdArray(data.map((row: any) => String(row?.id) ?? '') ?? []));
  };

  /* HANDLERS */
  const handleEdit = useCallback(
    (id: any, isDeleted: boolean) => {
      if (uiFlowMode === 'add') {
        uiFlowDispatch(setUIFlowMode('edit'));
      }

      uiFlowDispatch(pushNavigationStack(router.asPath));
      const basePath = determineUIFlowBasePath();

      router.push(`/${basePath}/${router.query.code}/${id}${isDeleted ? '?showDeleted=true' : ''}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uiFlowMode, router.asPath, router.query.code]
  );

  /**
   * Checks if an action has a specific permission.
   *
   * @param {string} componentId - The ID of the component.
   * @param {PermissionType} permission - The type of permission to check.
   * @return {boolean} Returns true if the action has the specified permission, otherwise false.
   */
  const actionHasPermission = (componentId: string, permission: PermissionType): boolean => {
    const res = getUiComponentPermissionByType(componentId, permission);

    return res;
  };

  const allowEdit = actionHasPermission(activeComponentId, 'CanUpdate');

  // only add delete if the page actually allows delete and then also apply permision check
  const allowDelete =
    defaultActions && defaultActions.canDelete ? actionHasPermission(activeComponentId, 'CanDelete') : false;

  const localDefaultActions: DefaultActions = useMemo(() => {
    if (defaultActions) {
      return {
        ...defaultActions,
        canEdit: !allowEdit ? false : defaultActions.canEdit === false ? false : true,
        editCallBack: handleEdit,
        canDelete: !allowDelete ? false : defaultActions.canDelete === false ? false : true,
        editToolTip: editToolTip ?? ''
      };
    }

    // if nothing is passed in we use edit
    return {
      canEdit: allowEdit,
      editCallBack: handleEdit,
      editToolTip: editToolTip ?? ''
    };
  }, [defaultActions, allowEdit, handleEdit, allowDelete, editToolTip]);

  /* EFFECTS */
  useEffect(() => {
    if (disableEffects || !dataSource) return;

    setUIFlowDataIdArray(dataSource);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource, uiFlowMode]);

  useEffect(() => {
    return () => {
      resetAttachmentCount();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading && <FallbackSpinner name="UIFlowListSpinner" />}
      {!loading && (
        <>
          <Card
            sx={{
              position: 'relative',
              height: '100%',
              backgroundColor: theme.palette.mode === 'light' ? 'transparent' : '#1e1e1e',
              boxShadow: contained === false ? '0px 5px 12px #00000029' : 'none'
            }}>
            <FilterPanel
              ref={filterPanelRef}
              filterCollection={filterCollection}
              filterColumn={filterColumn}
              onFilterItemSelected={onFilterItemSelected}
              filterDateRange={filterDateRange}
              customFilter={customFilter}
            />

            <Card
              sx={{
                position: 'relative',
                height: '100%',
                backgroundColor: theme.palette.mode === 'light' ? 'transparent' : '#1e1e1e',
                boxShadow: contained === false ? '0px 5px 12px #00000029' : 'none'
              }}>
              <NewDataGrid
                AdvancedSearch={AdvancedSearch}
                height={height ? height : `calc(100% - ${filterPanelRef?.current?.clientHeight ?? 0}px)`}
                keyExpr={keyExpr}
                name={listComponentName}
                columns={columns ? columns : []}
                datasource={dataSource}
                customStore={customStore}
                pagingOptions={pagingOptions}
                customActions={customActions}
                showColumnChooser={showColumnPicker}
                showFilterRow={showFilterRow}
                showHeaderFilter={showHeaderFilter}
                showFilterPanel={showFilterPanel}
                showGroupingPanel={showGroupingPanel}
                allowExport={enableExport}
                allowRowSelection={allowMultiSelect}
                showSearchPanel={enableSearch}
                disableLayoutReset={!enableLayoutReset}
                defaultActions={localDefaultActions}
                filter={filter}
                filterColumn={filterColumn}
                customToolbarComponents={headerButtonGroup}
                pagerVisible={!hideFooter}
                defaultGridState={defaultGridState}
                disableSelectionOnClick={disableSelectionOnClick}
                disableVirtualization={disableVirtualization}
                remoteOperations={remoteOperations}
                showRowLines={showRowLines}
                showColumnLines={showColumnLines}
                scrollMode={scrollMode}
                pageSize={pageSize ?? 500}
                hideHeader={hideHeader}
                onScrollBottom={onScrollEnd}
                showBorders={false}
                onRowSelection={multiSelectRowsIdsChange}
                onRowClick={gridRowClicked}
                onCellClick={onCellClick}
                onStateChange={onStateChange}
                hideActions={hideActions}
                ignoreDates={ignoreDates}
              />
            </Card>
          </Card>
        </>
      )}
    </>
  );
};

export default UIFlowListNew;
